"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
function NavContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  if (loading) {
    return <div className="flex flex-1 items-center justify-end">
        <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 gap-2">
          <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
          <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
          <div className="h-9 w-20 bg-muted animate-pulse rounded-md" />
        </div>
        <div className="h-9 w-32 bg-muted animate-pulse rounded-lg z-10" />
      </div>;
  }
  return <>
      {}
      <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <NavigationMenu viewport={false}>
          <NavigationMenuList>
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-foreground transition-all hover:text-primary hover:underline underline-offset-8 focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50">
                    {item.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {}
      <div className="flex items-center gap-4 z-10">
        <Button asChild variant="default" size="default" className="hidden sm:inline-flex rounded-lg shadow-md hover:scale-105 transition-transform font-default">
          <Link to="/HomePage">Reservar Cita</Link>
        </Button>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label={isMobileOpen ? "Cerrar menú" : "Abrir menú"} aria-expanded={isMobileOpen}>
          {isMobileOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </div>

      {}
      {isMobileOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border md:hidden flex flex-col p-4 gap-2 shadow-xl z-50 animate-in slide-in-from-top-2 duration-200">
          {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-base font-medium text-foreground hover:text-primary transition-colors py-3 border-b border-border/50 last:border-0 font-default" onClick={() => setIsMobileOpen(false)} data-index={i}>
              {item.label}
            </Link>)}
          <Button asChild variant="default" size="default" className="w-full mt-4 rounded-lg sm:hidden bg-primary text-primary-foreground font-default">
            <Link to="/HomePage" onClick={() => setIsMobileOpen(false)}>
              Reservar Cita
            </Link>
          </Button>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="265086" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-md" : "bg-background border-b border-border/50")}>
      <div className="bg-primary/5 border-b border-border/30">
        <div className="container mx-auto px-4 py-6 flex flex-col items-center gap-2 text-center">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-primary">
            Energias Renovables Cubanas
          </h1>
          <p className="text-muted-foreground max-w-2xl text-sm md:text-base font-medium">
            Nuestra misión es transformar biomasa y aceites usados en energía limpia para impulsar la soberanía energética de Cuba.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 h-20 flex items-center justify-between relative">
        <div className="flex-shrink-0 z-10">
          <Link to="/" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md block transition-transform hover:scale-105">
            <WvcLogo className="h-15 w-auto" />
          </Link>
        </div>
        
        <MenuProvider menu_id="16" className="flex-1 flex items-center justify-end static">
          <NavContent />
        </MenuProvider>
      </div>
    </section>;
}